import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

type Status = "loading" | "valid" | "already" | "invalid" | "success" | "error";

const Unsubscribe = () => {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [status, setStatus] = useState<Status>("loading");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    (async () => {
      try {
        const res = await fetch(
          `${SUPABASE_URL}/functions/v1/handle-email-unsubscribe?token=${encodeURIComponent(token)}`,
          { headers: { apikey: SUPABASE_ANON_KEY } }
        );
        const data = await res.json();
        if (data.valid) setStatus("valid");
        else if (data.reason === "already_unsubscribed") setStatus("already");
        else setStatus("invalid");
      } catch {
        setStatus("error");
      }
    })();
  }, [token]);

  const confirm = async () => {
    if (!token) return;
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "handle-email-unsubscribe",
        { body: { token } }
      );
      if (error) throw error;
      if (data?.success) setStatus("success");
      else if (data?.reason === "already_unsubscribed") setStatus("already");
      else setStatus("error");
    } catch {
      setStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-background px-4">
      <Helmet>
        <title>Unsubscribe | Golden State Project Works</title>
        <meta name="description" content="Unsubscribe from Golden State Project Works email updates." />
        <meta name="robots" content="noindex" />
        <link rel="canonical" href="https://lahaulingpros.com/unsubscribe" />
        <meta property="og:title" content="Unsubscribe | Golden State Project Works" />
        <meta property="og:description" content="Unsubscribe from Golden State Project Works email updates." />
        <meta property="og:url" content="https://lahaulingpros.com/unsubscribe" />
      </Helmet>
      <div className="max-w-md w-full text-center space-y-6 py-16">
        <h1 className="text-3xl font-bold text-foreground">Unsubscribe</h1>
        {status === "loading" && (
          <p className="text-muted-foreground">Checking your link…</p>
        )}
        {status === "valid" && (
          <>
            <p className="text-muted-foreground">
              Click below to confirm you want to stop receiving emails from LA Hauling Pros.
            </p>
            <Button onClick={confirm} disabled={submitting} size="lg">
              {submitting ? "Processing…" : "Confirm Unsubscribe"}
            </Button>
          </>
        )}
        {status === "already" && (
          <p className="text-muted-foreground">
            You're already unsubscribed. No further action needed.
          </p>
        )}
        {status === "success" && (
          <p className="text-muted-foreground">
            You've been unsubscribed. Sorry to see you go.
          </p>
        )}
        {status === "invalid" && (
          <p className="text-muted-foreground">
            This unsubscribe link is invalid or expired.
          </p>
        )}
        {status === "error" && (
          <p className="text-muted-foreground">
            Something went wrong. Please try again later.
          </p>
        )}
      </div>
    </main>
  );
};

export default Unsubscribe;
